import React, { createContext, useState, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const ToastContext = createContext();

let toastIdCounter = 0;

const TOAST_EXIT_DURATION = 150;

// 全局 toast 引用，供 store 等非组件代码使用
let _globalAddToast = null;
export const globalToast = {
  success: (msg, duration) => _globalAddToast?.(msg, 'success', duration),
  error: (msg, duration) => _globalAddToast?.(msg, 'error', duration),
  warning: (msg, duration) => _globalAddToast?.(msg, 'warning', duration),
  info: (msg, duration) => _globalAddToast?.(msg, 'info', duration),
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = ++toastIdCounter;
    // 自动消失计时交给 ToastItem，这样消失前能播出场动画
    setToasts(prev => [...prev, { id, message, type, duration }]);
    return id;
  }, []);

  // 注册全局引用，供 store 等非组件代码使用
  useEffect(() => {
    _globalAddToast = addToast;
    return () => { _globalAddToast = null; };
  }, [addToast]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = useMemo(() => ({
    success: (msg, duration) => addToast(msg, 'success', duration),
    error: (msg, duration) => addToast(msg, 'error', duration),
    warning: (msg, duration) => addToast(msg, 'warning', duration),
    info: (msg, duration) => addToast(msg, 'info', duration),
  }), [addToast]);

  const value = useMemo(() => ({ addToast, removeToast, toast }), [addToast, removeToast, toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    // 如果没有 ToastProvider，提供一个 no-op toast
    return {
      success: (msg) => console.log('[Toast]', msg),
      error: (msg) => console.error('[Toast Error]', msg),
      warning: (msg) => console.warn('[Toast Warning]', msg),
      info: (msg) => console.log('[Toast]', msg),
    };
  }
  return context.toast;
};

// 单个 Toast：负责自己的消失节奏，先播出场动画再通知父级移除
const ToastItem = ({ toast, config, onRemove }) => {
  const { Icon, color } = config;
  const [exiting, setExiting] = useState(false);
  const exitingRef = useRef(false);

  const beginExit = useCallback(() => {
    if (exitingRef.current) return;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      onRemove(toast.id);
      return;
    }
    exitingRef.current = true;
    setExiting(true);
    setTimeout(() => onRemove(toast.id), TOAST_EXIT_DURATION);
  }, [onRemove, toast.id]);

  useEffect(() => {
    if (!toast.duration || toast.duration <= 0) return undefined;
    const t = setTimeout(beginExit, toast.duration);
    return () => clearTimeout(t);
  }, [toast.duration, beginExit]);

  return (
    <div
      className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-lg shadow-xl border ${
        exiting
          ? 'animate-out fade-out slide-out-to-right-4 duration-150'
          : 'animate-in slide-in-from-right duration-300'
      }`}
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderColor: 'var(--color-border-primary)',
        color: 'var(--color-text-primary)',
      }}
      onClick={beginExit}
    >
      <Icon size={16} className="shrink-0" style={{ color }} />
      <span className="text-sm font-medium">{toast.message}</span>
      <button
        className="ml-2 opacity-60 hover:opacity-100 transition-opacity"
        style={{ color: 'var(--color-text-muted)' }}
        aria-label="关闭提示"
        onClick={(e) => { e.stopPropagation(); beginExit(); }}
      >
        <X size={14} />
      </button>
    </div>
  );
};

// Toast 容器组件
const ToastContainer = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  const typeConfig = {
    success: { Icon: CheckCircle2, color: 'var(--color-success)' },
    error: { Icon: XCircle, color: 'var(--color-error)' },
    warning: { Icon: AlertTriangle, color: 'var(--color-warning)' },
    info: { Icon: Info, color: 'var(--color-info)' },
  };

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts.map(toast => {
        const config = typeConfig[toast.type] || typeConfig.info;
        return (
          <ToastItem
            key={toast.id}
            toast={toast}
            config={config}
            onRemove={onRemove}
          />
        );
      })}
    </div>
  );
};
